import styled from '@emotion/styled';

import { focusRing, hoverable, pressable } from '../../interaction-states';
import Typography from '../Typography';

export const CardRoot = styled('article', {
  shouldForwardProp: (prop) => prop !== '$clickable',
})<{ $clickable: boolean }>(({ theme, $clickable }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  width: '100%',
  minWidth: 0,
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  background: theme.colors.background.raised,

  ...($clickable && {
    ...hoverable(theme, 'surfaceRaised'),
    cursor: 'pointer',
  }),

  // Same device as the highlighted Table row: a tint over the surface plus a leading brand strip.
  '&[data-highlight="true"]': {
    position: 'relative',
    backgroundImage: `linear-gradient(${theme.colors.brand.subtle}, ${theme.colors.brand.subtle})`,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: theme.radius[16],
      boxSizing: 'border-box',
      borderLeft: `2px solid ${theme.colors.brand.solid}`,
      borderRadius: `${theme.radius[16]}px 0 0 ${theme.radius[16]}px`,
      pointerEvents: 'none',
    },
  },
}));

export const CardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  minWidth: 0,
}));

export const CardLeading = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 40,
  height: 40,
  overflow: 'hidden',
  borderRadius: theme.radius[8],
  background: theme.colors.background.overlay,
  color: theme.colors.icon.secondary,

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

export const CardTitles = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  gap: theme.spacing[2],
  minWidth: 0,
}));

const ellipsis = {
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

export const CardTitle = styled(Typography)(ellipsis);

export const CardTitleButton = styled('button')(({ theme }) => ({
  ...hoverable(theme, 'underline'),
  ...focusRing(theme, { inset: false }),
  display: 'flex',
  minWidth: 0,
  padding: 0,
  border: 'none',
  borderRadius: theme.radius[4],
  background: 'none',
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
}));

export const CardCaption = styled(Typography)(({ theme }) => ({
  ...ellipsis,
  color: theme.colors.text.secondary,
}));

export const BadgeRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  minWidth: 0,
  overflow: 'hidden',
}));

export const CardDivider = styled('div')(({ theme }) => ({
  height: 1,
  flexShrink: 0,
  background: theme.colors.border.default,
}));

export const FieldRows = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
}));

export const DetailsInner = styled(FieldRows)(({ theme }) => ({
  paddingTop: theme.spacing[12],
}));

export const FieldRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing[16],
}));

export const Field = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  gap: theme.spacing[4],
  minWidth: 0,
  wordBreak: 'break-word',
}));

export const CardFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[12],

  '& svg': {
    width: 16,
    height: 16,
  },
}));

export const ToggleChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== '$open',
})<{ $open: boolean }>(({ $open }) => ({
  display: 'inline-flex',
  transform: $open ? 'rotate(180deg)' : undefined,
}));

export const SheetActionList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  padding: theme.spacing[4],
}));

export const SheetActionDivider = styled('div')(({ theme }) => ({
  height: 1,
  marginBlock: theme.spacing[4],
  background: theme.colors.border.default,
}));

export const SheetAction = styled('button', {
  shouldForwardProp: (prop) => prop !== '$destructive',
})<{ $destructive: boolean }>(({ theme, $destructive }) => ({
  ...hoverable(theme, 'surfaceRaised'),
  ...pressable(theme, 'surfaceStrong'),
  ...focusRing(theme),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  padding: theme.spacing[8],
  border: 'none',
  borderRadius: theme.radius[8],
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: $destructive ? theme.colors.text.error : theme.colors.text.primary,

  '& > svg, & > [data-icon]': {
    width: 20,
    height: 20,
    flexShrink: 0,
    color: $destructive ? theme.colors.icon.error : theme.colors.icon.primary,
  },

  '&:disabled': {
    cursor: 'not-allowed',
    color: theme.colors.text.disabled,
  },
}));
